/**
 * Onboarding questionnaire step machine (pure).
 * Order: intro1 → intro2 → age → name → leagues → done. `nextStep` returns null past the end
 * (caller routes to the first Monkey pick); `prevStep` returns null before the start
 * (caller exits back to the tutorial/welcome entry). The age step is skipped in both
 * directions once the account is verified 18+.
 *
 * See specs/onboarding-questionnaire/spec.md (Story 1/2/4/7, §9 T-4, T-13).
 */

export const QUESTIONNAIRE_STEPS = ['intro1', 'intro2', 'age', 'name', 'leagues', 'done'] as const;
export type QuestionnaireStep = (typeof QUESTIONNAIRE_STEPS)[number];

export interface StepContext {
  /** Already verified 18+: the age step is not shown. */
  ageVerified?: boolean;
}

function isSkipped(step: QuestionnaireStep, ctx: StepContext): boolean {
  return step === 'age' && ctx.ageVerified === true;
}

export function nextStep(step: QuestionnaireStep, ctx: StepContext = {}): QuestionnaireStep | null {
  let i = QUESTIONNAIRE_STEPS.indexOf(step) + 1;
  while (i < QUESTIONNAIRE_STEPS.length && isSkipped(QUESTIONNAIRE_STEPS[i], ctx)) i += 1;
  return QUESTIONNAIRE_STEPS[i] ?? null;
}

export function prevStep(step: QuestionnaireStep, ctx: StepContext = {}): QuestionnaireStep | null {
  let i = QUESTIONNAIRE_STEPS.indexOf(step) - 1;
  while (i >= 0 && isSkipped(QUESTIONNAIRE_STEPS[i], ctx)) i -= 1;
  return i >= 0 ? QUESTIONNAIRE_STEPS[i] : null;
}

/** Progress-bar fill (0..1). Only the three question steps show progress (age, name, leagues). */
export function questionnaireProgress(step: QuestionnaireStep): number {
  if (step === 'age') return 1 / 3;
  if (step === 'name') return 2 / 3;
  if (step === 'leagues') return 1;
  return 0;
}

/**
 * Whether the primary "Continue" is allowed for this step.
 * The username step gates on a confirmed-available username; every other step is free
 * (leagues can be skipped with none selected; the age step has its own submit).
 */
export function canAdvance(step: QuestionnaireStep, ctx: { usernameAvailable: boolean }): boolean {
  if (step === 'name') return ctx.usernameAvailable === true;
  return true;
}
